import chalk from 'chalk'
import { 
  fetchAllEntities,
  fetchEntityMetadata,
  type ProgressCallback
} from '../../client/index.js'
import { processEntityMetadata } from '../../processors/index.js'
import { generateMultipleEntityTypes } from '../../codegen/index.js'
import type { DataverseTypeGenConfig } from '../../config/index.js'

interface Entity {
  logicalName: string
  displayName: string
  schemaName: string
}

export async function runSimpleInteractiveGenerate(config: DataverseTypeGenConfig): Promise<boolean> {
  try {
    console.log(chalk.blue.bold('🚀 Interactive Dataverse Type Generator'))
    console.log('')
    
    // Step 1: Load entities
    console.log(chalk.cyan('📋 Loading available entities...'))
    const entityList = await fetchAllEntities()
    const entities: Entity[] = entityList.map(e => ({
      logicalName: e.LogicalName,
      displayName: e.DisplayName?.UserLocalizedLabel?.Label || e.LogicalName,
      schemaName: e.SchemaName
    }))
    
    console.log(chalk.green(`✅ Found ${entities.length} entities`))
    console.log('')
    
    // Step 2: Show sample entities
    console.log(chalk.yellow('📝 Sample entities available:'))
    entities.slice(0, 10).forEach((entity, index) => {
      console.log(`  ${index + 1}. ${entity.displayName} (${entity.logicalName})`)
    })
    if (entities.length > 10) {
      console.log(`  ... and ${entities.length - 10} more`)
    }
    console.log('')
    
    // For demo purposes, let's generate types for the first few custom entities
    const customEntities = entities.filter(e => 
      e.logicalName.includes('_') && 
      !e.logicalName.startsWith('msdyn_') &&
      !e.logicalName.startsWith('msft_')
    ).slice(0, 3)
    
    if (customEntities.length === 0) {
      console.log(chalk.yellow('⚠️  No custom entities found. Using first 3 system entities instead.'))
      customEntities.push(...entities.slice(0, 3))
    }
    
    console.log(chalk.blue('🎯 Generating types for these entities:'))
    customEntities.forEach(entity => {
      console.log(`  • ${entity.displayName} (${entity.logicalName})`)
    })
    console.log('')
    
    // Step 3: Fetch metadata with progress
    console.log(chalk.cyan('📡 Fetching entity metadata...'))
    let current = 0
    const total = customEntities.length
    
    const progressCallback: ProgressCallback = (curr, tot) => {
      current = curr
      const percentage = Math.round((curr / tot) * 100)
      const bar = '█'.repeat(Math.floor(percentage / 5)) + '░'.repeat(20 - Math.floor(percentage / 5))
      process.stdout.write(`\r  [${bar}] ${percentage}% (${curr}/${tot})`)
    }
    
    const metadataResults = []
    for (const entity of customEntities) {
      try {
        const metadata = await fetchEntityMetadata(entity.logicalName, { progressCallback })
        if (metadata) {
          metadataResults.push(metadata)
        }
        current++
        progressCallback(current, total)
      } catch (error) {
        console.log(chalk.red(`\n❌ Failed to fetch ${entity.logicalName}: ${error instanceof Error ? error.message : 'Unknown error'}`))
      }
    }
    
    console.log('\n' + chalk.green(`✅ Fetched metadata for ${metadataResults.length} entities`))
    console.log('')
    
    // Step 4: Process metadata
    console.log(chalk.cyan('⚙️  Processing metadata...'))
    const processedMetadata = metadataResults.map(result => processEntityMetadata(result))
    console.log(chalk.green(`✅ Processed ${processedMetadata.length} entities`))
    console.log('')
    
    // Step 5: Generate TypeScript types
    console.log(chalk.cyan('🔧 Generating TypeScript types...'))
    await generateMultipleEntityTypes(processedMetadata, config)
    console.log(chalk.green('✅ TypeScript types generated successfully!'))
    console.log('')
    
    // Step 6: Summary
    console.log(chalk.green.bold('🎉 Generation Complete!'))
    console.log(chalk.gray(`Generated types for ${processedMetadata.length} entities:`))
    processedMetadata.forEach(entity => {
      console.log(chalk.gray(`  • ${entity.displayName || entity.logicalName}`))
    })
    console.log('')
    console.log(chalk.blue(`📁 Files generated in: ${config.outputDir || './generated'}`))
    
    return true
    
  } catch (error) {
    console.log('')
    console.log(chalk.red.bold('❌ Generation failed!'))
    console.log(chalk.red(error instanceof Error ? error.message : 'Unknown error'))
    return false
  }
}